import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useMyTier } from "../features/subscriptions/useMyTier";
import NotificationBell from "./NotificationBell";
import TierBadge from "./TierBadge";
import { PageLoader } from "./ui";

const FULL_SCREEN_PATHS = ["/chat", "/dm"];
const isFullScreen = (pathname: string) =>
  FULL_SCREEN_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));

/**
 * Route shell that renders the shared nav/header but never redirects anonymous
 * visitors to /login. Authenticated users see the full nav (AI Chat, Profile,
 * tier badge, notifications); anonymous users see Login / Sign up buttons.
 *
 * Full-screen routes (chat / dm) render bare so the page owns its own chrome.
 */
const PublicShell: React.FC = () => {
  const { isAuthenticated, isAuthReady } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const tier = useMyTier();

  if (!isAuthReady) {
    return <PageLoader label="Loading…" />;
  }

  if (isFullScreen(location.pathname)) {
    return <Outlet />;
  }

  return (
    <div className="page-shell flex min-h-screen flex-col">
      <header className="app-navbar border-b border-border/80">
        <div className="app-navbar-inner">
          <button type="button" onClick={() => navigate("/")} className="app-brand">
            <span className="app-brand-mark" aria-hidden="true">
              G
            </span>
            <span className="app-brand-text">Travel Marketplace</span>
          </button>
          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <>
                <TierBadge tier={tier} className="hidden sm:inline-flex" />
                <button type="button" onClick={() => navigate("/community")} className="btn-ghost btn-sm">
                  Community
                </button>
                <button type="button" onClick={() => navigate("/chat")} className="btn-ghost btn-sm">
                  AI Chat
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/profile")}
                  className="btn-ghost btn-sm hidden sm:inline-flex"
                >
                  Profile
                </button>
                <NotificationBell />
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => navigate("/login", { state: { from: location.pathname } })}
                  className="btn-ghost btn-sm"
                >
                  Login
                </button>
                <button
                  type="button"
                  onClick={() =>
                    navigate("/login", { state: { from: location.pathname, mode: "signup" } })
                  }
                  className="btn-primary btn-sm"
                >
                  Sign up
                </button>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
};

export default PublicShell;
